import React, { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, animate } from "framer-motion";

export default function ResizableBox({
  direction = "horizontal", // 'horizontal' or 'vertical'
  initialSize = 268,
  minSize = 150,
  maxSize = 500,
  className = "",
  children,
  resize_handle_position = "bottom", // 'bottom' or 'top' for vertical
  isShowHandler = true,
}) {
  const [size, setSize] = useState(initialSize);
  const motionSize = useMotionValue(initialSize);
  const isResizing = useRef(false);
  const boxRef = useRef(null);

  const startResizing = () => (isResizing.current = true);
  const stopResizing = () => (isResizing.current = false);

  const handleResize = (e) => {
    if (!isResizing.current || !boxRef.current) return;

    const boxRect = boxRef.current.getBoundingClientRect();

    if (direction === "horizontal") {
      const newWidth = Math.min(
        Math.max(e.clientX - boxRect.left, minSize),
        maxSize
      );
      motionSize.set(newWidth);
    } else {
      let newHeight;

      if (resize_handle_position === "top") {
        // Calculate from bottom edge when handle is on top
        const distanceFromBottom = boxRect.bottom - e.clientY;
        newHeight = Math.min(Math.max(distanceFromBottom, minSize), maxSize);
      } else {
        // Normal (bottom handle)
        const distanceFromTop = e.clientY - boxRect.top;
        newHeight = Math.min(Math.max(distanceFromTop, minSize), maxSize);
      }

      motionSize.set(newHeight);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => {
      if (isResizing.current) {
        setSize(motionSize.get());
        animate(motionSize, motionSize.get(), {
          duration: 0.25,
          ease: "easeOut",
        });
      }
      stopResizing();
    };

    window.addEventListener("mousemove", handleResize);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [direction, resize_handle_position]);

  return (
    <motion.div
      ref={boxRef}
      className={`relative bg-[#F1F1F1] ${className}`}
      style={
        direction === "horizontal"
          ? { width: motionSize, height: "100%" }
          : { height: motionSize, width: "100%" }
      }
      transition={{ type: "spring", stiffness: 180, damping: 20 }}
    >
      {/* Content */}
      <div className="w-full h-full overflow-hidden">{children}</div>

      {/* Resize Handle */}

      {isShowHandler && (
        <div>
          {direction === "horizontal" ? (
            <div
              onMouseDown={startResizing}
              className="absolute top-0 right-0 h-full cursor-col-resize flex items-center justify-center"
            >
              <div className="h-[56px] w-1 rounded-full bg-black/30 hover:bg-[#50546a] transition-colors" />
            </div>
          ) : (
            <div
              onMouseDown={startResizing}
              className={`absolute left-1/2 -translate-x-1/2 w-full border-y border-[#C9C9C9] cursor-row-resize flex justify-center bg-white py-0.5
          ${resize_handle_position === "top" ? "bottom-full" : "bottom-0"}`}
            >
              <div className="w-[56px] h-1 rounded-full bg-black/30 hover:bg-[#50546a] transition-colors" />
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
